import { Injectable, Inject } from "@angular/core";
import { ENV_VARS } from "../environment-variables/environment-variables.module";
import { Store } from "@ngrx/store";
import { AppState } from "../models/app.state";
import { FhirService } from "./fhir.service";
import {
  QuestionnaireService,
  AnswerInfo,
  OptionObject,
  QuestionnaireResponseService
} from "@zitelab/ionic-fhir-questionnaires";
import { WidgetConfig } from "@zitelab/ionic-fhir-questionnaires/dist/models/widget";
import {
  DIAPROFIL_QUESTIONNAIRE_UUID,
  PatientHistory,
  AnswerChange,
  AnswerColorChangeCode,
  WidgetAnswerHistory,
  changeCodeMapping,
  changeCodeIcons,
  IDiaprofilHcp,
  IConsultationStatusDetails,
  CFD_DIAGNOSIS_TIMEPOINT_QUESTIONNAIRE_UUID,
  CONSULTATION_TOPICS_VALUESET_UUID
} from "../models/types";
import { forkJoin } from "rxjs/observable/forkJoin";
import { switchMap, delay } from "rxjs/operators";
import { of } from "rxjs/observable/of";
import { diaprofilEnableWhenFunctions } from "../utils/enable-when-functions";
import { Subscription } from "rxjs/Subscription";
import { Observable } from "rxjs/Observable";
import { extractResponseValidationExt } from "../utils/extract-validation-ext";
import * as moment from "moment";

@Injectable()
export class DashboardService {
  widgets: WidgetConfig<any>[] = null;
  widgetsByTopic: { [key: string]: WidgetConfig<any>[] };
  hcpUsersSubscription: Subscription;
  hcpUsers: IDiaprofilHcp[] = [];
  constructor(
    public store: Store<AppState>,
    private fhir: FhirService,
    public questionnaireService: QuestionnaireService,
    public questionnaireResponseService: QuestionnaireResponseService,
    @Inject(ENV_VARS) public envVars
  ) {
    this.hcpUsersSubscription = this.store
      .select(state => state.hcpUsers)
      .subscribe(hcpUsers => (this.hcpUsers = hcpUsers));
  }

  findWidgetsForResponse(
    questionnaireResource: fhir.Questionnaire,
    flattenedAnswers: {},
    taskId: string = null
  ) {
    return this.questionnaireService.getWidgets({
      resourceId: questionnaireResource.id,
      item: questionnaireResource.item,
      existingValues: flattenedAnswers,
      metadata: {
        questionnaireId: questionnaireResource.id,
        questionnaireVersion: questionnaireResource.meta.versionId,
        taskId: taskId
      }
    });
  }

  setGenericProWidgets(questionnaireResource: fhir.Questionnaire) {
    this.widgets = this.questionnaireService.getWidgets({
      resourceId: questionnaireResource.id,
      item: questionnaireResource.item,
      metadata: {
        questionnaireId: questionnaireResource.id,
        questionnaireVersion: questionnaireResource.meta.versionId,
        taskId: null
      }
    });
    this.widgetsByTopic = this.widgets.reduce((acc, widget) => {
      let widgetTopic = widget.valueBasedHealthTopic.topic;
      if (widgetTopic === null) {
        widgetTopic = "NO_TOPIC";
      }
      if (!acc.hasOwnProperty(widgetTopic)) {
        acc[widgetTopic] = [];
      }
      acc[widgetTopic] = [...acc[widgetTopic], ...[widget]];
      return acc;
    }, {});
    return this.widgets;
  }

  getDefinitions() {
    const queries: Observable<fhir.Bundle>[] = [
      this.fhir.getQuery({
        resourceType: "Questionnaire",
        queryParams: { _count: 999 }
      }),
      this.fhir.getQuery({
        resourceType: "ValueSet",
        queryParams: { _count: 999 }
      })
    ];
    return forkJoin(queries).pipe(
      switchMap(bundles => {
        bundles.map(bundle => {
          if (bundle.total > 0) {
            const resources = bundle.entry.map(entry => entry.resource);
            this.fhir.addResourcesToState({
              resources: resources,
              overwrite: true,
              resolveRefs: false
            });
          }
        });
        return of(true);
      }),
      delay(1000),
      switchMap(result => {
        const questionnaireResource = this.fhir.resolveReference(
          "Questionnaire/" + DIAPROFIL_QUESTIONNAIRE_UUID
        ) as fhir.Questionnaire;
        this.setGenericProWidgets(questionnaireResource);
        return of(true);
      })
    );
  }

  getValueSetOptions(valueSetUuid): OptionObject[] {
    const valueSet = this.questionnaireService.resolveFhirReference(
      "ValueSet/" + valueSetUuid
    ) as fhir.ValueSet;
    const options = this.questionnaireService.flattenValueSetConcepts(
      valueSet.compose.include
    );
    return options;
  }

  getConsultationTopicOptions(): OptionObject[] {
    return this.getValueSetOptions(CONSULTATION_TOPICS_VALUESET_UUID);
  }

  getWidgetsWithAnswers(responseResource: fhir.QuestionnaireResponse) {
    if (this.widgets === null) {
      const questionnaireResource = this.fhir.resolveReference(
        `Questionnaire/${DIAPROFIL_QUESTIONNAIRE_UUID}`
      ) as fhir.Questionnaire;
      this.setGenericProWidgets(questionnaireResource);
    }
    const extractedAnswers = this.questionnaireResponseService.extractAnswersFromQuestionnaireResponse(
      responseResource
    );
    const flattenedAnswers = this.questionnaireResponseService.flattenQuestionnaireAnswers(
      extractedAnswers
    );
    const withAnswers = this.widgets.map(widget => {
      return {
        config: widget,
        answerInfo: this.questionnaireResponseService.getItemAnswerInfo(
          widget,
          flattenedAnswers,
          diaprofilEnableWhenFunctions
        )
      };
    });
    return withAnswers;
  }

  calculateAnswerChange(
    widget: WidgetConfig<any>,
    cur: {
      source: fhir.QuestionnaireResponse;
      answerInfo: AnswerInfo;
    },
    compare: {
      source: fhir.QuestionnaireResponse;
      answerInfo: AnswerInfo;
    },
    compareType: "PREVIOUS" | "NEXT"
  ): AnswerChange {
    if (compare === null) {
      return {
        responseId: null,
        date: null,
        answerInfo: {
          rawAnswer: "NO_ANSWER",
          displayAnswer: "Intet tidligere svar",
          optionObj: {
            code: "NO_ANSWER",
            display: "Intet tidligere svar",
            designation: [
              { language: "da-DK", value: "Intet tidligere svar" },
              { language: "en-UK", value: "No previous answer" }
            ],
            colorInterpretation: "NONE",
            index: 0
          } as OptionObject,
          colorCode: "dashboard-item-no-answer",
          isEnabled: null,
        },
        changeCode: "NOTHING_TO_COMPARE_WITH",
        changeCodeIcon: "information"
      };
    }
    if (
      widget.widgetType === "StringWidget" ||
      widget.widgetType === "TextAreaWidget"
    ) {
      return {
        responseId: null,
        date: null,
        answerInfo: {
          rawAnswer: "NO_ANSWER",
          displayAnswer: "Kan ikke sammenligne tekst svar",
          optionObj: {
            code: "NO_ANSWER",
            display: "Kan ikke sammenligne tekst svar",
            designation: [
              { language: "da-DK", value: "Kan ikke sammenligne tekst svar" },
              { language: "en-UK", value: "Kan ikke sammenligne tekst svar" }
            ],
            colorInterpretation: "NONE",
            index: 0
          } as OptionObject,
          colorCode: "dashboard-item-no-answer",
          isEnabled: null
        },
        changeCode: "CANNOT_CALCULATE",
        changeCodeIcon: "information"
      };
    }
    let changeCode: AnswerColorChangeCode;
    let newestValue, oldestValue;
    if (
      Array.isArray(cur.answerInfo.colorCode) ||
      Array.isArray(compare.answerInfo.colorCode)
    ) {
      changeCode = "CANNOT_CALCULATE";
    } else {
      if (compareType === "NEXT") {
        newestValue = cur.answerInfo.colorCode as string;
        oldestValue = compare.answerInfo.colorCode as string;
      } else {
        oldestValue = cur.answerInfo.colorCode as string;
        newestValue = compare.answerInfo.colorCode as string;
      }
      if (oldestValue === undefined || newestValue === undefined) {
        changeCode = "CANNOT_CALCULATE";
      } else {
        console.log({ widget, newestValue, oldestValue });
        changeCode = changeCodeMapping[newestValue][oldestValue];
      }
    }
    return {
      responseId: compare.source.id,
      date: compare.source.authored,
      answerInfo: compare.answerInfo,
      changeCode: changeCode,
      changeCodeIcon: changeCodeIcons[changeCode]
    };
  }

  getHistoryForWidget(
    patientHistory: PatientHistory,
    widget: WidgetConfig<any>
  ): WidgetAnswerHistory {
    if (patientHistory === undefined) {
      return {
        widget: widget,
        answers: []
      };
    }
    const widgetAnswers = patientHistory.map(response => {
      const relItem = response.items.find(
        item => item.config.name === widget.name
      );
      return {
        source: response.responseResource,
        answerInfo: relItem.answerInfo
      };
    });
    const withChanges = widgetAnswers.map((elem, index) => {
      return {
        ...elem,
        change: {
          prev: this.calculateAnswerChange(
            widget,
            elem,
            widgetAnswers[index - 1] || null,
            "PREVIOUS"
          ),
          next: this.calculateAnswerChange(
            widget,
            elem,
            widgetAnswers[index + 1] || null,
            "NEXT"
          )
        }
      };
    });
    return {
      widget: widget,
      answers: withChanges
    };
  }

  getConsultationStatus(
    responseResource: fhir.QuestionnaireResponse
  ): Observable<IConsultationStatusDetails> {
    return this.fhir
      .getQuery({
        resourceType: "Task",
        queryParams: {
          code: "|parentTask",
          intent: "HCP_POST_CONSULTATION",
          "based-on": `QuestionnaireResponse/${responseResource.id}`,
          "part-of:missing": "true"
        }
      })
      .pipe(
        switchMap(result => {
          const qrExt = extractResponseValidationExt(responseResource);
          if (result.total > 0) {
            const hcpEvalTask = result.entry[0].resource as fhir.Task;
            return of({
              ...qrExt,
              ...{
                hcpEvalTask: hcpEvalTask
              }
            });
          } else {
            return of(qrExt);
          }
        })
      );
  }

  getDiagnosisTimepointResponse(
    associatedResponse: fhir.QuestionnaireResponse
  ): Observable<OptionObject> {
    const minDate = moment(associatedResponse.authored).format("YYYY-MM-DD");
    const maxDate = moment(associatedResponse.authored)
      .add(1, "day")
      .format("YYYY-MM-DD");
    const questionnaire = this.fhir.resolveReference(
      `Questionnaire/${CFD_DIAGNOSIS_TIMEPOINT_QUESTIONNAIRE_UUID}`
    ) as fhir.Questionnaire;
    return this.fhir
      .getQuery({
        resourceType: "QuestionnaireResponse",
        queryString: [
          `author=${associatedResponse.author.reference}`,
          `questionnaire=Questionnaire/${CFD_DIAGNOSIS_TIMEPOINT_QUESTIONNAIRE_UUID}`,
          `authored=ge${minDate}&authored=le${maxDate}`,
          "_count=1"
        ].join("&")
      })
      .pipe(
        switchMap(bundle => {
          if (bundle.total === 1) {
            const responseResource = bundle.entry[0]
              .resource as fhir.QuestionnaireResponse;
            const responses = this.questionnaireResponseService.getQuestionnaireResponseAnswers(
              questionnaire,
              responseResource
            );
            const itemMap = responses.reduce((prev, cur, i) => {
              prev[cur.config.name] = cur;
              return prev;
            }, {});
            let value: OptionObject =
              itemMap["whenDidYouGetDiagnosis"].answerInfo.rawAnswer ===
              "WITHIN_LAST_YEAR"
                ? itemMap["diagnosisTimeWithinLastYear"].answerInfo.optionObj
                : itemMap["whenDidYouGetDiagnosis"].answerInfo.optionObj;
            return of(value);
          }
          return of({
            code: "NOT_ANSWERABLE",
            display: "Skulle ikke besvares",
            designation: [
              { language: "da-DK", value: "Skulle ikke besvares" },
              { language: "en-UK", value: "Not answerable" }
            ],
            colorInterpretation: "NONE",
            index: 0
          } as OptionObject);
        })
      );
  }
}



// WEBPACK FOOTER //
// ./src/app/services/dashboard.service.ts